import { BellOutlined, CheckOutlined, DownOutlined } from "@ant-design/icons"
import { Dropdown, Modal, type MenuProps } from "antd"
import React, { useEffect, useRef } from "react"
import { type TFunction, useTranslation } from "react-i18next"

import {
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission
} from "../../../../../utils/notification/browser-notification.ts"
import { subscribeToWebPush } from "../../../../../utils/notification/web-push.ts"

// Minutes before start, matching ezbuild_back's validate_reminder_offset
// (null, or an integer in [1, 43200]) exactly — a curated preset list rather
// than a free-typed number, since nothing in the product asks for an
// arbitrary offset and a preset can never fall outside that range. "None"
// sits last, not first: 5 minutes is the default for a new event, and the
// explicit opt-out reads more naturally at the end of the list.
export const REMINDER_OFFSET_PRESETS: { value: number | null }[] = [
  { value: 5 },
  { value: 15 },
  { value: 30 },
  { value: 60 },
  { value: 1440 },
  { value: null }
]

// A value outside the curated presets (a legacy record, mobile, or a direct
// API write — the backend accepts any integer in [1, 43200]) must never read
// as "None": that would tell the user no reminder exists when one really
// does, so it falls back to the raw minute count rather than the None copy.
const labelFor = (value: number | null, t: TFunction) => {
  switch (value) {
    case null:
      return t("notifications.reminder.none")
    case 60:
      return t("notifications.reminder.hourBefore")
    case 1440:
      return t("notifications.reminder.dayBefore")
    default:
      return t("notifications.reminder.minutesBefore", { count: value })
  }
}

interface CalendarAddReminderOffsetFieldProps {
  value?: number | null
  onChange?: (value: number | null) => void
  className?: string
  // A fresh event's reminder is defaulted to 5 minutes via the form's
  // `initialValues`, never through this field's own onChange — so the
  // majority case (the user submits the default untouched) would otherwise
  // never ask for desktop-notification permission at all. Set only for a
  // brand-new event (never on edit, where the value is the record's own
  // saved preference, already asked about or not, and re-asking on every
  // open would be a real prompt-spam regression).
  promptOnMountIfSet?: boolean
  // Picking a value here means "this is what a future event should start
  // with" (Calendar Settings' default-reminder picker), not "remind me
  // about this real thing now" — the desktop-permission prompt belongs to
  // the latter. Default true so every existing call site (the event/reminder
  // forms) is unaffected.
  promptForDesktop?: boolean
}

// The right moment to ask for desktop-notification permission is here, not
// on app load — the user has just expressed they want to be reminded of
// something. Silently skipped once they've already answered (or the browser
// doesn't support it at all), so this never re-prompts.
const promptForDesktopNotifications = (t: TFunction) => {
  if (getDesktopNotificationPermission() !== "default") return

  Modal.confirm({
    title: t("notifications.reminder.permissionTitle"),
    content: t("notifications.reminder.permissionBody"),
    okText: t("notifications.reminder.permissionOk"),
    cancelText: t("notifications.reminder.permissionCancel"),
    onOk: async () => {
      const permission = await requestDesktopNotificationPermission()
      // Web Push (service worker + subscription) is what survives the
      // browser being fully closed. It rides on top of the plain
      // Notification permission granted above rather than gating it — if
      // registration or subscribing fails for any reason (unsupported
      // browser, offline, backend unreachable), the plain-Notification path
      // still delivers reminders while a tab is open, so this is never
      // awaited by the permission flow itself.
      if (permission === "granted") void subscribeToWebPush()
    }
  })
}

// A single antd Select nested inside a Popover needed two separate clicks —
// one to open the popover, a second to open the Select's own internal
// dropdown — since a Select always manages its own popup regardless of what
// contains it. Rendering the option list directly as a Dropdown menu (no
// intermediate Select) collapses that to one click, one dropdown.
export function CalendarAddReminderOffsetField({
  value,
  onChange,
  className,
  promptOnMountIfSet = false,
  promptForDesktop = true
}: CalendarAddReminderOffsetFieldProps) {
  const { t } = useTranslation()
  const current = value ?? null
  const currentLabel = labelFor(current, t)

  const handleSelect = (next: number | null) => {
    if (next !== null && promptForDesktop) promptForDesktopNotifications(t)
    onChange?.(next)
  }

  // Runs once, on mount only (empty deps) — a later value change already
  // goes through handleSelect above, which is the real "user just enabled a
  // reminder" moment for every case except this one.
  const hasPromptedOnMount = useRef(false)
  useEffect(() => {
    if (hasPromptedOnMount.current) return
    hasPromptedOnMount.current = true
    if (promptOnMountIfSet && current !== null) promptForDesktopNotifications(t)
  }, [])

  const items: MenuProps["items"] = REMINDER_OFFSET_PRESETS.map((preset) => ({
    key: String(preset.value),
    label: (
      <span className="flex min-w-32 items-center justify-between gap-4">
        {labelFor(preset.value, t)}
        {preset.value === current && (
          <CheckOutlined className="text-xs text-primary" />
        )}
      </span>
    )
  }))

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomLeft"
      menu={{
        items,
        selectedKeys: [String(current)],
        onClick: ({ key }) => {
          const preset = REMINDER_OFFSET_PRESETS.find(
            (p) => String(p.value) === key
          )
          handleSelect(preset?.value ?? null)
        }
      }}
    >
      <button
        type="button"
        aria-label={t("notifications.reminder.trigger")}
        className={className}
      >
        <BellOutlined className="text-gray-400" />
        {currentLabel}
        <DownOutlined className="text-[10px]" />
      </button>
    </Dropdown>
  )
}
